import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import {
    ArrowRightOnRectangleIcon,
    Bars3Icon,
    ChartBarIcon,
    Cog6ToothIcon,
    FolderArrowDownIcon,
    FolderIcon,
    GlobeAltIcon,
    InformationCircleIcon,
    LanguageIcon,
    PencilSquareIcon,
    TvIcon,
    UserGroupIcon,
    UsersIcon,
    WifiIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { getAuth } from "firebase/auth";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../context/Language/LanguageContext";
import AppLogo from "../assets/VBLive_Logo_Horizontal.png";
import { useAuthStatus } from "../hooks/useAuthStatus";
import { isAdminUser, isAllowedEmailUser } from "../utils/access";
import { classNames } from "../utils/Utils";

// The main menu items. Content items open the corresponding panel on MainPage
// (via location state); route items navigate to a standalone page.
const baseNav = [
  { name: "Matches", content: 0, icon: FolderIcon },
  { name: "Live", content: 1, icon: WifiIcon },
  { name: "Import", content: 2, icon: FolderArrowDownIcon },
  { name: "Playlists", content: 3, icon: TvIcon },
  { name: "New Match", content: 4, icon: PencilSquareIcon },
  { name: "Teams", route: "/teams", icon: UserGroupIcon },
  { name: "Players", route: "/players", icon: UsersIcon },
];

/**
 * A hamburger button + slide-out drawer that mirrors the MainPage sidebar, so
 * the full main menu is reachable from the standalone pages (Teams, Players,
 * Session, AI Reports). `current` is the active route (e.g. "/teams") used to
 * highlight the matching item.
 */
export default function AppMenu({ current }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { t, lang, setLang, allLanguages } = useLanguage();
  const { firebaseUser } = useAuthStatus();

  const navItems = isAllowedEmailUser(firebaseUser)
    ? baseNav
    : [
        ...baseNav,
        { name: "AI Reports", route: "/aireports", icon: ChartBarIcon },
      ];
  const fullNavItems = isAdminUser(firebaseUser)
    ? [...navItems, { name: "Admin", route: "/admin", icon: Cog6ToothIcon }]
    : navItems;

  const go = (item) => {
    setOpen(false);
    if (item.route) {
      navigate(item.route);
    } else {
      navigate("/mainpage", { state: { currentContent: item.content } });
    }
  };

  const doLogout = () => {
    setOpen(false);
    getAuth().signOut();
    navigate("/signin");
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn btn-ghost btn-sm -ml-2"
        aria-label={t("Open menu")}
      >
        <Bars3Icon aria-hidden="true" className="size-6" />
      </button>

      <Dialog open={open} onClose={setOpen} className="relative z-50">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
        />
        <div className="fixed inset-0 flex">
          <DialogPanel
            transition
            className="relative flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
          >
            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-base-100 px-6 pb-4">
              <div className="flex h-16 shrink-0 items-center justify-between">
                <img alt="VBLive" src={AppLogo} className="h-[32px] w-auto" />
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="btn btn-ghost btn-sm"
                  aria-label={t("Close menu")}
                >
                  <XMarkIcon aria-hidden="true" className="size-6" />
                </button>
              </div>
              <nav className="flex flex-1 flex-col">
                <ul role="list" className="-mx-2 space-y-1">
                  {fullNavItems.map((item) => {
                    const active = item.route && current === item.route;
                    return (
                      <li key={item.name}>
                        <button
                          type="button"
                          onClick={() => go(item)}
                          className={classNames(
                            active
                              ? "bg-primary/60 text-base-primary"
                              : "text-base-primary hover:bg-primary/20",
                            "group flex w-full items-center gap-x-3 rounded-md p-2 text-sm/6 font-semibold",
                          )}
                        >
                          <item.icon
                            aria-hidden="true"
                            className="size-6 shrink-0"
                          />
                          {t(item.name)}
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <div className="mt-auto space-y-1 border-t border-base-200 pt-3">
                  <div className="-mx-2 mb-1 flex items-center gap-2 rounded-md px-2 py-1">
                    <GlobeAltIcon className="size-5 shrink-0 text-base-content/40" />
                    <select
                      className="select select-ghost select-xs flex-1 text-sm font-semibold"
                      value={lang}
                      onChange={(e) => setLang(e.target.value)}
                      aria-label={t("Language")}
                    >
                      {Object.entries(allLanguages).map(
                        ([code, { label, flag }]) => (
                          <option key={code} value={code}>
                            {flag} {label}
                          </option>
                        ),
                      )}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => go({ name: "Translation Editor", route: "/translation" })}
                    className="group -mx-2 flex w-full items-center gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-base-primary hover:bg-primary/20"
                  >
                    <LanguageIcon
                      aria-hidden="true"
                      className="size-6 shrink-0"
                    />
                    {t("Translation Editor")}
                  </button>
                  <button
                    type="button"
                    onClick={() => go({ name: "Information", route: "/information" })}
                    className="group -mx-2 flex w-full items-center gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-base-primary hover:bg-primary/20"
                  >
                    <InformationCircleIcon
                      aria-hidden="true"
                      className="size-6 shrink-0"
                    />
                    {t("Information")}
                  </button>
                  <button
                    type="button"
                    onClick={() => go({ name: "Settings", route: "/settings" })}
                    className="group -mx-2 flex w-full items-center gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-base-primary hover:bg-primary/20"
                  >
                    <Cog6ToothIcon
                      aria-hidden="true"
                      className="size-6 shrink-0"
                    />
                    {t("Settings")}
                  </button>
                  <button
                    type="button"
                    onClick={doLogout}
                    className="group -mx-2 flex w-full items-center gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-base-primary hover:bg-primary/20"
                  >
                    <ArrowRightOnRectangleIcon
                      aria-hidden="true"
                      className="size-6 shrink-0"
                    />
                    {t("Sign out")}
                  </button>
                </div>
              </nav>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
}
